import { useEffect } from 'react';
import { MDText } from 'i18n-react';
import noop from 'lodash/noop';

import { Modal } from '@motorway/motorway-storybook-cra';

import {
	OtpFormContent,
	OtpFormContentBottom,
	OtpSuccessFormContent,
} from 'Components/AuthenticationFeedback/AuthenticationFeedback';
import OTPContent from 'Components/pages/VehiclePayment/components/OTPContent/OTPContent';
import type { OTPContentProps } from 'Components/pages/VehiclePayment/components/OTPContent/OTPContent.types';
import { OtpAction } from 'Components/pages/VehiclePayment/components/OTPContent/OTPContent.types';
import { onRequestCatch } from 'Components/pages/VehiclePayment/components/OTPContent/OTPRequestCatch.helper';
import { GA_OTP_LABEL, OTP_ACTION } from 'Components/pages/VehiclePayment/VehiclePayment.const';
import Texts from 'Components/texts.json';
import {
	CLICK_SCHEMA,
	POPUP_APPEAR_SCHEMA,
	triggerOtpAnalyticsEvent,
} from 'Services/analytics/events/paymentEvents/otpSnowplowEvents';
import { confirmOTPPhoneNumber } from 'Services/api/otp';
import { useStoreOtpPhoneValidated } from 'Utilities/hooks/useStoreOtpPhoneValidated';
import useUser from 'Utilities/hooks/useUser';

import { type OtpSetupModalProps, OtpSetupModalVariant } from './OtpSetupModal.types';

import styles from './OtpSetupModal.module.scss';

const LocalT = new MDText(Texts);

export const OtpSetupModal: React.FC<OtpSetupModalProps> = ({
	onDismiss,
	onSuccess = noop,
	origin = GA_OTP_LABEL.OTP_SETUP_SHOWN,
	show,
	variant,
}) => {
	const { otpPhone } = useUser();
	const [, setIsOtpPhoneValidated] = useStoreOtpPhoneValidated(false);

	const endOfPhone = otpPhone?.slice?.(otpPhone.length - 3, otpPhone.length) || '';
	const showSuccessOtpSetup = variant === OtpSetupModalVariant.SUCCESS;

	const handleSetUpMFA: OTPContentProps['request'] = async (verificationCode) => {
		try {
			await confirmOTPPhoneNumber({ params: { otpAction: OtpAction.VERIFY_PHONE, verificationCode } });

			return () => {
				setIsOtpPhoneValidated(true);
				onSuccess();
			};
		} catch (error) {
			throw await onRequestCatch({ error });
		}
	};

	const handleOnClose = () => {
		triggerOtpAnalyticsEvent({
			customData: {
				label: origin,
			},
			name: OTP_ACTION.OTP_SETUP_CLOSED,
			schema: CLICK_SCHEMA,
		});
		onDismiss();
	};

	const handleOnPhoneClick = () => {
		triggerOtpAnalyticsEvent({
			customData: {
				label: GA_OTP_LABEL.OTP_PHONE_NUMBER_CLICKED,
			},
			name: OTP_ACTION.OTP_PHONE_NUMBER_CLICKED,
			schema: CLICK_SCHEMA,
		});
	};

	useEffect(() => {
		if (!show) return;
		if (showSuccessOtpSetup) return;

		triggerOtpAnalyticsEvent({
			customData: {
				label: origin,
			},
			name: OTP_ACTION.OTP_SETUP_SHOWN,
			schema: POPUP_APPEAR_SCHEMA,
		});
	}, [origin, showSuccessOtpSetup, show]);

	if (!show) return null;

	return (
		<Modal dismissModal={handleOnClose} shouldCloseOnOverlayClick={false} size="small">
			{showSuccessOtpSetup ? (
				<OtpSuccessFormContent origin={origin} />
			) : (
				<OTPContent
					bottomDesc={
						<div className={styles.bottomDesc}>
							<OtpFormContentBottom onPhoneClick={handleOnPhoneClick} />
						</div>
					}
					desc={<OtpFormContent otpPhoneEnd={endOfPhone} />}
					logs={{ origin, source: 'setup' }}
					request={handleSetUpMFA}
					title={`${LocalT.translate('MFA.titleOTP')}`}
				/>
			)}
		</Modal>
	);
};
